import { Component, ElementRef, forwardRef, HostListener, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { ThemeService } from 'src/app/services/theme.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-multi-select-autocomplete',
  templateUrl: './icam-multi-select-autocomplete.component.html',
  styleUrls: ['./icam-multi-select-autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IcamMultiSelectAutocomplete),
      multi: true
    }
  ]
})
export class IcamMultiSelectAutocomplete implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @Input() options: any[] = [];
  @Input() placeholder = 'Search...';
  @Input() displayField = 'name';
  @Input() filterFields: string[] = ['name'];
  @Input() fetchFn!: (searchTerm: string) => Observable<any[]>;

  filteredOptions: any[] = [];
  selectedItems: any[] = [];
  searchControl = new FormControl('');
  isDropdownOpen = false;
  isLoading = false;
  errorMessage = '';
  isLight = true;

  private searchTerms = new Subject<string>();
  private destroy$ = new Subject<void>();
  private onChange: (value: any) => void = () => {
    /* noop */
  };
  private _onTouched: () => void = () => {
    /* noop */
  };

  constructor(private _themeService: ThemeService, private _elementRef: ElementRef) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this._elementRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && !changes['options'].firstChange) {
      this.refreshFilteredOptions();
    }
  }

  ngOnInit() {
    this._themeService.isLight$.pipe(takeUntil(this.destroy$)).subscribe((isLight) => (this.isLight = isLight));

    this.searchTerms
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => {
          this.isLoading = true;
          this.errorMessage = '';
        }),
        switchMap((term) =>
          this.fetchFn(term).pipe(
            catchError(() => {
              this.errorMessage = 'Failed to load results';
              this.isLoading = false;
              return of([]);
            })
          )
        ),
        tap(() => (this.isLoading = false))
      )
      .subscribe((results) => {
        this.filteredOptions = this.filterExistingItems(results);
        this.isDropdownOpen = this.filteredOptions.length > 0;
      });

    this.searchControl.valueChanges.subscribe((value) => {
      if (typeof value === 'string') {
        this.searchTerms.next(value.trim());
      }
    });

    // Initialize filteredOptions immediately if options are already loaded
    this.refreshFilteredOptions();
  }

  private filterExistingItems(items: any[]): any[] {
    return items.filter((item) => !this.selectedItems.some((selected) => selected.id === item.id));
  }

  private refreshFilteredOptions(): void {
    const term = (this.searchControl.value || '').toLowerCase();
    const filtered = term
      ? this.options.filter((o) => this.filterFields.some((f) => o[f]?.toLowerCase().includes(term)))
      : this.options;
    this.filteredOptions = this.filterExistingItems(filtered);
  }

  writeValue(value: any[]): void {
    this.selectedItems = value || [];
    // Don't call onChange here - writeValue is for external updates only
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  filterOptions(searchTerm: string) {
    if (!searchTerm) {
      this.filteredOptions = this.options;
      return;
    }

    const term = searchTerm.toLowerCase();
    this.filteredOptions = this.options.filter((option) =>
      this.filterFields.some((field) => option[field]?.toLowerCase().includes(term))
    );
  }

  selectItem(item: any) {
    if (!this.selectedItems.some((selected) => selected.id === item.id)) {
      this.selectedItems = [...this.selectedItems, item];
      this.onChange(this.selectedItems);
    }
    this.searchControl.setValue('');
    this.isDropdownOpen = false;
  }

  removeItem(item: any) {
    this.selectedItems = this.selectedItems.filter((selected) => selected.id !== item.id);
    this.onChange(this.selectedItems);
  }

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
    if (this.isDropdownOpen) {
      const term = this.searchControl.value || '';
      if (!term) {
        this.refreshFilteredOptions();
      } else {
        this.searchTerms.next(term);
      }
    }
  }
  handleClickOutside() {
    this.isDropdownOpen = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
